<template>
  <div class="content-wrapper">
    <NavBar class="navbar">
      <template #center>
        <span>{{this.localizedData.title_long}}</span>
      </template>
      <template #right>
        <span class="navbar-svg-right" @click="toggleFullscreen">
          <svg fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path fill-rule="evenodd"
                  :d="isFullscreen? 'M11 4.5a.5.5 0 0 0 .5.5H15a1 1 0 1 1 0 2h-3.5A2.5 2.5 0 0 1 9 4.5V1a1 1 0 1 1 2 0zm-6 7a.5.5 0 0 0-.5-.5H1a1 1 0 1 1 0-2h3.5A2.5 2.5 0 0 1 7 11.5V15a1 1 0 1 1-2 0z' : 'M3.5 13a.5.5 0 0 1-.5-.5V8a1 1 0 1 0-2 0v4.5A2.5 2.5 0 0 0 3.5 15H8a1 1 0 1 0 0-2zm9-10a.5.5 0 0 1 .5.5V8a1 1 0 1 0 2 0V3.5A2.5 2.5 0 0 0 12.5 1H8a1 1 0 0 0 0 2z'" clip-rule="evenodd"/>
          </svg>
        </span>
      </template>
    </NavBar>
    <section class="content">
      <iframe
          id="gameFrame"
          class="game-frame"
          :src="frameLink('xsolla')"
          allow="fullscreen"
      ></iframe>

      <div
          v-if="screenWidth > 720 && !isFullscreen"
          :class="['ads-container', 'ads-right', { 'has-ad': adRightVisible }]"
      >
        <AdsContainer
            :maxWidth="'300px'"
            :maxHeight="'100%'"
            @hide="onAdRightHide"
            @show="onAdRightShow"
            key="right"
            :adType="1"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { computed } from 'vue'
import GoogleAnalytics from '@/src/googleanalytics'
import portalData from './../../portal.json'
import { useHead } from '@vueuse/head'
import NavBar from '@/components/navbar.vue'
import AdsContainer from '@/components/adscontainer.vue'

export default {
  name: 'Play',
  components: { NavBar, AdsContainer },
  data() {
    return {
      portalData,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      debugData: '',
      adRefresh: 0,
      _adMql: null,
      _lastMatch: null,
      adRightVisible: true,
      isFullscreen: false,
    }
  },
  beforeMount() {
    useHead(computed(() => this.head))
  },
  mounted() {
    // Fix for mobile viewport unit issue
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`)
    }
    setVh()
    window.addEventListener('resize', setVh)
    window.addEventListener('orientationchange', setVh)
    this._setVh = setVh

    window.scrollTo({ top: 0, behavior: 'smooth' })
    document.body.style.overflow = 'hidden'
    this.updateScreenSize()
    window.addEventListener('resize', this.updateScreenSize)
    window.addEventListener('orientationchange', this.updateScreenSize)
    this._adMql = window.matchMedia('(min-width: 930px)')
    this._lastMatch = this._adMql.matches
    this._adMql.addEventListener('change', this._onAdBreakpointChange)

    // Listen for fullscreen events
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)

    if (this.$route.params.game) {
      GoogleAnalytics.setGameParams(
          this.$route.params.game,
          this.localizedData.title_short
      )
    }
  },
  unmounted() {
    document.body.style.overflow = ''
    useHead({})
    window.removeEventListener('resize', this.updateScreenSize)
    window.removeEventListener('orientationchange', this.updateScreenSize)
    if (this._adMql) {
      this._adMql.removeEventListener('change', this._onAdBreakpointChange)
    }
    window.removeEventListener('resize', this._setVh)
    window.removeEventListener('orientationchange', this._setVh)
    // Remove fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
  },
  watch: {
    screenWidth(newVal, oldVal) {
      if (newVal > 720 && oldVal <= 720) {
        this.adRightVisible = true
      }
    }
  },
  methods: {
    updateScreenSize() {
      this.screenWidth = window.innerWidth
      this.screenHeight = window.innerHeight
    },
    _onAdBreakpointChange(e) {
      if (e.matches !== this._lastMatch) {
        this._lastMatch = e.matches
        this.adRefresh++
      }
    },
    frameLink() {
      let link = this.portalData.games[this.$route.params.game].links.xsolla
      if (process.env.VUE_APP_MODE === 'staging') {
        link = link
            .replace('cleverappssg.com', 'labsystech.ru')
            .replace('.labsystech.ru', '-staging.labsystech.ru')
            .replace('html5', 'html5-staging')
      }
      if (
          process.env.VUE_APP_MODE === 'production' &&
          navigator.language.includes('ru')
      ) {
        link = link.replace(
            '/?source=cleverapps',
            '/index-ru2.html?source=cleverapps'
        )
      }
      link += '?source=xsolla'
      const params = new URLSearchParams(window.location.search)
      if (params.get('channel')) link += '&channel=' + params.get('channel')
      if (params.get('push_code')) link += '&push_code=' + params.get('push_code')
      return link
    },
    getGameBanner(wide) {
      const images = require.context(
          './../../assets/banners',
          false,
          /\.jpg$/
      )
      return images(
          './' + this.$route.params.game + (wide ? '_wide.jpg' : '.jpg')
      )
    },
    onAdRightHide() {
      this.adRightVisible = false
    },
    onAdRightShow() {
      this.adRightVisible = true
    },
    toggleFullscreen() {
      const elem = this.$el;
      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
          elem.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { /* IE11 */
          document.msExitFullscreen();
        }
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      );
    }
  },
  computed: {
    head() {
      if (!this.$route.params.game) return {}
      return {
        title: this.localizedData.title_long,
        meta: [
          {name: 'description', content: this.localizedData.description_text_1},
          {property: 'og:title', content: this.localizedData.title_long},
          {property: 'og:site_name', content: 'Clever Apps Games'},
          {property: 'og:description', content: this.localizedData.description_text_2},
          {property: 'og:type', content: 'website'},
          {
            property: 'og:url',
            content:
                'https://cleverappssg.com/play/' +
                this.$route.params.game +
                '/' +
                this.lang +
                '/'
          },
          {property: 'og:image', content: this.getGameBanner()},
          {property: 'twitter:title', content: this.localizedData.title_long},
          {property: 'twitter:description', content: this.localizedData.description_text_2},
          {property: 'twitter:card', content: this.getGameBanner()}
        ],
        link: Object.keys(this.portalData.languages).map(lang => ({
          rel: lang === this.lang ? 'canonical' : 'alternate',
          hreflang: lang,
          href:
              'https://cleverappssg.com/play/' + this.$route.params.game
        }))
      }
    },
    lang() {
      return this.language
    },
    localizedData() {
      const data = this.portalData.games[this.$route.params.game]
      const result = {}
      Object.keys(data).forEach(key => {
        const val = data[key]
        result[key] = typeof val === 'object' && val[this.lang] ? val[this.lang] : val
      })
      return result
    },
    debugInfo() {
      return this.debugData
    },
    isLandscape() {
      return window.matchMedia('(orientation: landscape)').matches
    },
    isPortrait() {
      return window.matchMedia('(orientation: portrait)').matches
    },
    haveNotch() {
      return ['--sat', '--sal', '--sar', '--sab']
          .some(varName =>
              getComputedStyle(document.documentElement).getPropertyValue(varName) !== '0px'
          )
    }
  }
}
</script>

<style scoped>
span {
  color: white;
}

.navbar-svg-right svg {
  display: inline-block;
  vertical-align: middle;
  width: 24px;
  height: 24px;
  fill: white;
  cursor: pointer;
}

.content-wrapper {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: var(--vh);
  min-height: 0;
}

.content {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}

.game-frame {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: block;
  border: 0;
}

.ads-container {
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 10;
}

.ads-right {
  /* No fixed size unless has-ad */
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;
  transition: height 0.2s;
}

.ads-right.has-ad {
  width: 300px;
  max-width: 250px;
  height: 100%;
}

.ads-right {
  flex-shrink: 0;
}

@media (max-width: 720px) {
  .content {
    flex-direction: column;
  }
}
</style>
